import React, { useState, useEffect, useRef } from 'react';
import { fetchFiles } from '../api/projectFilesApi';

/**
 * FileSearchModal Component
 * Spotlight-style global search overlay with real-time API queries and instant navigation
 */
export default function FileSearchModal({
    isOpen,
    onClose,
    recentSearches = [],
    onSelectSearchResult,
}) {
    const [query, setQuery] = useState('');
    const [remoteFiles, setRemoteFiles] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [navigatingId, setNavigatingId] = useState(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setNavigatingId(null);
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setQuery('');
            setRemoteFiles([]);
            setNavigatingId(null);
        }
    }, [isOpen]);

    // Live debounced server-side file search
    useEffect(() => {
        if (!query.trim()) {
            setRemoteFiles([]);
            setIsSearching(false);
            return;
        }

        const timer = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await fetchFiles({ search: query.trim() });
                if (res?.success && Array.isArray(res.data)) {
                    setRemoteFiles(res.data);
                } else {
                    setRemoteFiles([]);
                }
            } catch (err) {
                console.error('[FileSearchModal] Search error:', err);
                setRemoteFiles([]);
            } finally {
                setIsSearching(false);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                if (isOpen) {
                    onClose?.();
                }
            } else if (e.key === 'Escape' && isOpen) {
                onClose?.();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleItemClick = (item, uniqueKey) => {
        if (navigatingId) return;
        setNavigatingId(uniqueKey);

        // Show brief fake spinner so user gets instant visual confirmation that the click registered
        setTimeout(() => {
            onSelectSearchResult?.(item);
            onClose?.();
            setNavigatingId(null);
        }, 450);
    };

    if (!isOpen) return null;

    // Combine matching project records and live server-queried files
    const projectMatches = query.trim()
        ? recentSearches.filter((item) =>
            item.title.toLowerCase().includes(query.toLowerCase()) ||
            item.type.toLowerCase().includes(query.toLowerCase())
        )
        : recentSearches;

    const fileMatches = remoteFiles.map((f) => ({
        ...f,
        title: f.name || f.originalName,
        type: 'File',
        icon: f.icon || 'description',
        iconColor: f.iconColor || 'text-[#0070F3]',
    }));

    const totalResults = query.trim() ? (projectMatches.length + fileMatches.length) : projectMatches.length;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-[#0b1c30]/50 backdrop-blur-sm"
                onClick={() => {
                    if (!navigatingId) onClose?.();
                }}
            />

            {/* Modal Dialog */}
            <div className="file-search-modal relative top-16 w-full max-w-2xl bg-white dark:bg-[#141c2b] rounded-xl shadow-2xl overflow-hidden border border-[#c1c6d7] dark:border-[#344258] animate-in fade-in-0 zoom-in-95 duration-150">
                {/* Search Bar Header */}
                <div className="p-4 sm:p-5 border-b border-[#c1c6d7] dark:border-[#344258] flex items-center gap-3">
                    <span className="material-symbols-outlined text-[#0070F3] text-2xl">
                        search
                    </span>
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search files globally across all projects, folders, or clients..."
                        className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-base font-medium text-[#0b1c30] dark:text-[#eaf1ff] placeholder:text-[#727786]"
                    />
                    {isSearching ? (
                        <span className="material-symbols-outlined text-lg text-[#0070F3] animate-spin">
                            progress_activity
                        </span>
                    ) : (
                        <div className="flex items-center gap-1 px-2 py-0.5 bg-[#eff4ff] dark:bg-[#1a2538] rounded text-[11px] font-semibold text-[#727786] dark:text-[#9aa5bc] border border-[#c1c6d7]/50">
                            <span>Esc</span>
                        </div>
                    )}
                </div>

                {/* Results / Recent Searches */}
                <div className="p-4 sm:p-5 max-h-[420px] overflow-y-auto files-custom-scrollbar">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xs font-bold text-[#555d73] dark:text-[#9aa5bc] uppercase tracking-wider">
                            {query.trim() ? `Search Results (${totalResults})` : 'Recent Projects'}
                        </h3>
                    </div>

                    {totalResults === 0 ? (
                        <div className="py-12 text-center text-xs text-[#727786]">
                            {isSearching ? 'Searching database...' : `No matching projects or files found for "${query}"`}
                        </div>
                    ) : (
                        <div className="space-y-1">
                            {/* Project matches */}
                            {projectMatches.map((item, index) => {
                                const itemKey = `proj-${item.id || item.title || index}`;
                                const isNavigating = navigatingId === itemKey;
                                return (
                                    <div
                                        key={itemKey}
                                        onClick={() => handleItemClick(item, itemKey)}
                                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer group transition-all ${
                                            isNavigating
                                                ? 'bg-blue-50/80 dark:bg-[#1a2538] ring-1 ring-[#0070F3]'
                                                : 'hover:bg-[#eff4ff] dark:hover:bg-[#1a2538]'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            {isNavigating ? (
                                                <span className="material-symbols-outlined text-[#0070F3] text-xl animate-spin">
                                                    progress_activity
                                                </span>
                                            ) : (
                                                <span className="material-symbols-outlined text-[#727786] group-hover:text-[#0070F3] text-xl transition-colors">
                                                    {item.icon || 'work'}
                                                </span>
                                            )}
                                            <div className="truncate">
                                                <span className="text-sm font-semibold text-[#0b1c30] dark:text-[#eaf1ff] block truncate">
                                                    {item.title}
                                                </span>
                                                <span className="text-[11px] text-[#727786] dark:text-[#9aa5bc]">
                                                    Project Workspace
                                                </span>
                                            </div>
                                        </div>
                                        {isNavigating ? (
                                            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-100/70 dark:bg-blue-900/40 text-[#0070F3] dark:text-[#60a5fa] font-bold text-xs border border-blue-300 dark:border-blue-800">
                                                <span className="material-symbols-outlined text-xs animate-spin">
                                                    progress_activity
                                                </span>
                                                <span>Opening...</span>
                                            </div>
                                        ) : (
                                            <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-[#0070F3] dark:text-[#60a5fa] font-bold border border-blue-200 dark:border-blue-900/50">
                                                Project
                                            </span>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Global file matches */}
                            {fileMatches.map((item, index) => {
                                const itemKey = `file-${item.id || item._id || item.title || index}`;
                                const isNavigating = navigatingId === itemKey;
                                return (
                                    <div
                                        key={itemKey}
                                        onClick={() => handleItemClick(item, itemKey)}
                                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer group transition-all border-t border-slate-100 dark:border-slate-800/60 ${
                                            isNavigating
                                                ? 'bg-blue-50/80 dark:bg-[#1a2538] ring-1 ring-[#0070F3]'
                                                : 'hover:bg-[#eff4ff] dark:hover:bg-[#1a2538]'
                                        }`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            {isNavigating ? (
                                                <span className="material-symbols-outlined text-[#0070F3] text-xl animate-spin">
                                                    progress_activity
                                                </span>
                                            ) : (
                                                <span className={`material-symbols-outlined text-xl ${item.iconColor || 'text-[#0070F3]'}`}>
                                                    {item.icon || 'description'}
                                                </span>
                                            )}
                                            <div className="truncate">
                                                <span className="text-sm font-semibold text-[#0b1c30] dark:text-[#eaf1ff] block truncate" title={item.title}>
                                                    {item.title}
                                                </span>
                                                <div className="flex items-center gap-2 text-[11px] text-[#727786] dark:text-[#9aa5bc] mt-0.5">
                                                    <span className="truncate max-w-[180px] font-medium text-slate-700 dark:text-slate-300">
                                                        {item.project || 'General'}
                                                    </span>
                                                    <span>•</span>
                                                    <span>{item.subfolderName || `Folder ${item.folderCode}`}</span>
                                                    {item.size && (
                                                        <>
                                                            <span>•</span>
                                                            <span>{item.size}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                            {isNavigating ? (
                                                <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-100/70 dark:bg-blue-900/40 text-[#0070F3] dark:text-[#60a5fa] font-bold text-xs border border-blue-300 dark:border-blue-800">
                                                    <span className="material-symbols-outlined text-xs animate-spin">
                                                        progress_activity
                                                    </span>
                                                    <span>Opening...</span>
                                                </div>
                                            ) : (
                                                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-[#212f45] text-slate-700 dark:text-[#9aa5bc] border border-slate-200 dark:border-slate-700">
                                                    {item.folderCode ? `0${item.folderCode}`.slice(-2) : 'File'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Modal Footer with Keyboard Shortcuts */}
                <div className="bg-[#eff4ff] dark:bg-[#1a2538] px-5 py-2.5 flex justify-between items-center border-t border-[#c1c6d7] dark:border-[#344258]">
                    <div className="flex items-center gap-4 text-xs text-[#727786] dark:text-[#9aa5bc]">
                        <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">keyboard_return</span>
                            <span>to select</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">keyboard_arrow_up</span>
                            <span className="material-symbols-outlined text-sm">keyboard_arrow_down</span>
                            <span>to navigate</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-xs font-bold text-[#0070F3] hover:underline cursor-pointer"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
